/**
 * Mission Storage API Client
 * Handles saving, loading, and managing mission data using backend SQLite database
 * Replaces localStorage with server-side persistent storage
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';
const STORAGE_VERSION = '1.0';

/**
 * Mission data structure (same as before)
 */
export const createMissionData = (missionInfo, analysisData, fracAdaptData = null) => {
  return {
    id: generateMissionId(),
    version: STORAGE_VERSION,
    
    // Mission Information
    name: missionInfo.missionName,
    startDate: missionInfo.startDate,
    duration: missionInfo.duration,
    selectedVehicle: missionInfo.selectedVehicle,
    vehicleInfo: missionInfo.vehicleInfo,
    additionalCargoWeight: missionInfo.additionalCargoWeight || 0,
    routeProfile: missionInfo.routeProfile,
    
    // Location Data
    locationData: {
      startMarker: missionInfo.startMarker,
      destinationMarker: missionInfo.destinationMarker,
      startLocationName: missionInfo.startLocationName,
      destinationLocationName: missionInfo.destinationLocationName,
      routes: missionInfo.routes,
      selectedRouteIndex: missionInfo.selectedRouteIndex,
      terrainType: missionInfo.terrainType
    },
    
    // Weather Data
    weatherData: missionInfo.weatherData,
    
    // Analysis results
    analysisData: analysisData,
    
    // FracAdapt alternatives (if available)
    fracAdaptData: fracAdaptData,
    
    // Mission status and metadata
    status: 'planned',
    tags: [],
    notes: '',
    
    // Vehicle health prediction
    predictedHealth: null,
    
    // Vehicle application tracking
    appliedToVehicle: null,
    completionPercentage: 0,
    actualDistanceTraveled: 0,
    
    // Reroute tracking
    reroutes: [],
    originalRoute: null,
    
    // Statistics for quick overview
    summary: {
      totalDistance: analysisData.cardA.totalDistance,
      estimatedDuration: analysisData.cardA.estimatedDuration,
      fuelEstimate: analysisData.cardA.fuelEstimate,
      complexityScore: analysisData.cardE.missionComplexityScore,
      complexityLevel: analysisData.cardE.overallComplexity.level
    }
  };
};

/**
 * Generate unique mission ID
 */
const generateMissionId = () => {
  return `mission_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Save mission to backend
 */
export const saveMission = async (missionData) => {
  try {
    const response = await fetch(`${API_URL}/api/missions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
      },
      body: JSON.stringify(missionData),
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to save mission');
    }
    
    return {
      success: true,
      missionId: result.data.id,
      isUpdate: false
    };
  } catch (error) {
    console.error('Failed to save mission:', error);
    return {
      success: false,
      error: error.message || 'Failed to save mission to server'
    };
  }
};

/**
 * Transform mission from database format to app format
 */
const transformMissionFromDB = (mission) => {
  // Database stores flat structure, app expects nested missionInfo
  return {
    ...mission,
    missionInfo: {
      name: mission.name,
      startDate: mission.startDate,
      duration: mission.duration,
      selectedVehicle: mission.selectedVehicle,
      vehicleInfo: mission.vehicleInfo,
      additionalCargoWeight: mission.additionalCargoWeight || 0,
      routeProfile: mission.routeProfile
    }
  };
};

/**
 * Get all saved missions (summary mode for faster loading)
 */
export const getSavedMissions = async () => {
  try {
    const response = await fetch(`${API_URL}/api/missions?summary=true`, {
      headers: {
        'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
      }
    });
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to fetch missions');
    }
    
    // Transform missions to expected format
    const missions = result.data || [];
    return missions.map(transformMissionFromDB);
  } catch (error) {
    console.error('Failed to load saved missions:', error);
    return [];
  }
};

/**
 * Get mission by ID
 */
export const getMissionById = async (missionId) => {
  try {
    const response = await fetch(`${API_URL}/api/missions/${missionId}`, {
      headers: {
        'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
      }
    });
    const result = await response.json();
    
    if (!response.ok) {
      return null;
    }
    
    return result.data ? transformMissionFromDB(result.data) : null;
  } catch (error) {
    console.error('Failed to load mission:', error);
    return null;
  }
};

/**
 * Delete mission by ID
 */
export const deleteMission = async (missionId) => {
  try {
    const response = await fetch(`${API_URL}/api/missions/${missionId}`, {
      method: 'DELETE',
      headers: {
        'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
      }
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to delete mission');
    }
    
    return {
      success: true,
      deletedCount: 1
    };
  } catch (error) {
    console.error('Failed to delete mission:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete mission'
    };
  }
};

/**
 * Update mission metadata (name, notes, tags, status)
 */
export const updateMissionMetadata = async (missionId, updates) => {
  try {
    const response = await fetch(`${API_URL}/api/missions/${missionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
      },
      body: JSON.stringify(updates),
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to update mission');
    }
    
    return { success: true, mission: result.data };
  } catch (error) {
    console.error('Failed to update mission metadata:', error);
    return { success: false, error: error.message || 'Failed to update mission' };
  }
};

/**
 * Search missions by name, tags, or notes
 */
export const searchMissions = async (query) => {
  try {
    const response = await fetch(`${API_URL}/api/missions/search/${encodeURIComponent(query)}`, {
      headers: {
        'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
      }
    });
    const result = await response.json();
    
    if (!response.ok) {
      return [];
    }
    
    const missions = result.data || [];
    return missions.map(transformMissionFromDB);
  } catch (error) {
    console.error('Failed to search missions:', error);
    return [];
  }
};

/**
 * Get missions by status
 */
export const getMissionsByStatus = async (status) => {
  try {
    const response = await fetch(`${API_URL}/api/missions?status=${status}`, {
      headers: {
        'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
      }
    });
    const result = await response.json();
    
    if (!response.ok) {
      return [];
    }
    
    const missions = result.data || [];
    return missions.map(transformMissionFromDB);
  } catch (error) {
    console.error('Failed to fetch missions by status:', error);
    return [];
  }
};

/**
 * Get mission statistics from already-loaded missions (no API call)
 */
export const getMissionStatistics = (missions = []) => {
  try {
    const stats = {
      total: missions.length,
      byStatus: {
        planned: 0,
        applied: 0,
        'in-progress': 0,
        completed: 0,
        cancelled: 0
      },
      byComplexity: {
        low: 0,
        moderate: 0,
        high: 0,
        'very high': 0
      },
      totalDistance: 0,
      totalFuel: 0,
      averageComplexity: 0
    };
    
    missions.forEach(mission => {
      // Status counts
      stats.byStatus[mission.status] = (stats.byStatus[mission.status] || 0) + 1;
      
      // Complexity counts
      const complexity = mission.summary.complexityLevel.toLowerCase();
      stats.byComplexity[complexity] = (stats.byComplexity[complexity] || 0) + 1;
      
      // Totals
      const distance = parseFloat(mission.summary.totalDistance) || 0;
      const fuel = parseFloat(mission.summary.fuelEstimate) || 0;
      
      stats.totalDistance += distance;
      stats.totalFuel += fuel;
      stats.averageComplexity += mission.summary.complexityScore || 0;
    });
    
    if (missions.length > 0) {
      stats.averageComplexity = Math.round(stats.averageComplexity / missions.length);
    }
    
    return stats;
  } catch (error) {
    console.error('Failed to calculate statistics:', error);
    return {
      total: 0,
      byStatus: {},
      byComplexity: {},
      totalDistance: 0,
      totalFuel: 0,
      averageComplexity: 0
    };
  }
};

/**
 * Export mission data as JSON
 */
export const exportMission = async (missionId) => {
  const mission = await getMissionById(missionId);
  if (!mission) {
    return { success: false, error: 'Mission not found' };
  }
  
  try {
    const exportData = {
      ...mission,
      exportedAt: new Date().toISOString(),
      exportVersion: STORAGE_VERSION
    };

    // Strip raw elevation grids from each route before export.
    // The grid is a large SRTM raster (up to ~19MB) used only during terrain
    // analysis — all processed outputs (profile, ascent/descent, wear rates)
    // are already stored separately. The header metadata is kept.
    if (exportData.locationData?.routes) {
      exportData.locationData = {
        ...exportData.locationData,
        routes: exportData.locationData.routes.map(route => {
          if (!route.rawElevationData?.grid) return route;
          const { grid, ...headerOnly } = route.rawElevationData;
          return { ...route, rawElevationData: headerOnly };
        })
      };
    }

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    return {
      success: true,
      blob: dataBlob,
      filename: `mission_${mission.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.json`
    };
  } catch (error) {
    console.error('Failed to export mission:', error);
    return { success: false, error: 'Failed to export mission data' };
  }
};

/**
 * Import mission data from JSON
 */
export const importMission = async (jsonData) => {
  try {
    const missionData = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
    
    // Generate new ID to avoid conflicts
    delete missionData.id;
    delete missionData.createdAt;
    delete missionData.updatedAt;
    
    const result = await saveMission(missionData);
    
    return {
      success: result.success,
      missionId: result.missionId,
      error: result.error
    };
  } catch (error) {
    console.error('Failed to import mission:', error);
    return { success: false, error: 'Failed to parse mission data' };
  }
};

/**
 * Get storage usage information from already-loaded missions (no API call)
 */
export const getStorageInfo = (missions = []) => {
  try {
    return {
      missionCount: missions.length,
      storageSize: 0, // Server-side storage, size not tracked
      storageSizeFormatted: 'Server Storage',
      lastUpdated: missions.length > 0 ? missions[0].updatedAt : null
    };
  } catch (error) {
    console.error('Failed to get storage info:', error);
    return {
      missionCount: 0,
      storageSize: 0,
      storageSizeFormatted: 'Server Storage',
      lastUpdated: null
    };
  }
};

/**
 * Apply mission to vehicle
 */
export const applyMissionToVehicle = async (missionId, vehicleInfo, appliedBy = 'User') => {
  try {
    const updates = {
      status: 'applied',
      appliedToVehicle: {
        vehicleId: vehicleInfo.id,
        vehicleInfo: {
          brand: vehicleInfo.brand,
          model: vehicleInfo.model,
          type: vehicleInfo.type,
          uniqueId: vehicleInfo.uniqueId
        },
        appliedAt: new Date().toISOString(),
        appliedBy: appliedBy
      }
    };
    
    return await updateMissionMetadata(missionId, updates);
  } catch (error) {
    console.error('Failed to apply mission to vehicle:', error);
    return { success: false, error: 'Failed to apply mission' };
  }
};

/**
 * Start mission
 */
export const startMission = async (missionId, vehicleId, vehicleHealth, vehicleInfo) => {
  try {
    const response = await fetch(`${API_URL}/api/missions/${missionId}/start`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
      },
      body: JSON.stringify({ vehicleId, vehicleHealth, vehicleInfo })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to start mission');
    }
    
    return result;
  } catch (error) {
    console.error('Failed to start mission:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Complete mission
 * Simplified: No location or health parameters needed (uses predicted stats)
 */
export const completeMission = async (missionId) => {
  try {
    const response = await fetch(`${API_URL}/api/missions/${missionId}/complete`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-API-Key': process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5'
      }
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to complete mission');
    }
    
    return result;
  } catch (error) {
    console.error('Failed to complete mission:', error);
    return { success: false, error: error.message };
  }
};

/**
 * DEPRECATED: getActiveMissionForVehicle - Removed in simplified workflow
 */
export const getActiveMissionForVehicle_DEPRECATED = async (vehicleId) => {
  try {
    // This endpoint no longer exists
    return { success: false, hasActiveMission: false };
  } catch (error) {
    console.error('Failed to check active mission:', error);
    return null;
  }
};

/**
 * DEPRECATED: checkMissionConflict - Removed in simplified workflow
 */
export const checkMissionConflict = async (vehicleId, missionStartDate, missionEndDate) => {
  // No longer checking conflicts in simplified workflow
  return { hasConflict: false };
};

/**
 * Add reroute event to mission
 * DEPRECATED: Reroute functionality removed in simplified workflow
 */
export const addRerouteToMission = async (missionId, rerouteData) => {
  console.warn('addRerouteToMission is deprecated in simplified workflow');
  return { success: false, error: 'Reroute functionality has been removed' };
};

/**
 * DEPRECATED: calculateCompletionPercentage - Removed in simplified workflow
 */
export const calculateCompletionPercentage = (startCoords, currentCoords, endCoords) => {
  console.warn('calculateCompletionPercentage is deprecated in simplified workflow');
  return {
    percentage: 0,
    traveledDistance: 0,
    totalDistance: 0,
    remainingDistance: 0
  };
};
